<template>
    <!-- Render as anchor tag if href is provided -->
    <a 
        v-if="href"
        :href="href"
        :target="target"
        :rel="rel"
        :class="buttonClasses"
        :aria-disabled="disabled || loading"
        :data-bs-toggle="modalToggle"
        :data-bs-target="modalTarget"
    >
        <!-- Loading spinner -->
        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        
        <!-- Icon before text -->
        <i v-if="icon && !iconOnly" :class="icon" :aria-hidden="true"></i>
        
        <!-- Icon only (no text) -->
        <i v-if="icon && iconOnly" :class="icon" :aria-hidden="true"></i>
        
        <!-- Text content -->
        <span v-if="!iconOnly">{{ text }} <slot></slot></span>
        
        <!-- Loading text -->
        <span v-if="loading && loadingText">{{ loadingText }}</span>
    </a>
    
    <!-- Render as button if no href -->
    <button 
        type="button" 

        v-else
        :class="buttonClasses" 
        :disabled="disabled || loading"
        :data-bs-toggle="modalToggle"
        :data-bs-target="modalTarget"
        @click="handleClick"
    >
        <!-- Loading spinner -->
        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        
        <!-- Icon before text -->
        <i v-if="icon && !iconOnly" :class="icon" :aria-hidden="true"></i>
        
        <!-- Icon only (no text) -->
        <i v-if="icon && iconOnly" :class="icon" :aria-hidden="true"></i>
        
        <!-- Text content -->
        <span v-if="!iconOnly">{{ text }} <slot></slot></span>
        
        <!-- Loading text -->
        <span v-if="loading && loadingText">{{ loadingText }}</span>
    </button>
</template>

<script>
import { defineComponent, computed } from 'vue'

export default defineComponent({
    name: 'Button',
    props: {
        text: {
            type: String,
            default: ''
        },
        type: {
            type: String,
            default: 'primary',
            validator: (value) => [
                'primary', 'secondary', 'success', 'danger', 'warning', 
                'info', 'light', 'dark', 'outline-primary', 'outline-secondary',
                'outline-success', 'outline-danger', 'outline-warning', 
                'outline-info', 'outline-light', 'outline-dark'
            ].includes(value)
        },
        size: {
            type: String,
            default: 'md',
            validator: (value) => ['sm', 'md', 'lg'].includes(value)
        },
        icon: {
            type: String,
            default: null
        },
        iconOnly: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        loadingText: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        href: {
            type: String,
            default: null
        },
        target: {
            type: String,
            default: null,
            validator: (value) => !value || ['_blank', '_self', '_parent', '_top'].includes(value)
        },
        modal: {
            type: String,
            default: null
        }
    },
    emits: ['click'],
    computed: {
        buttonClasses() {
            const classes = ['btn']
            
            // Add button type class
            classes.push(`btn-${this.type}`)
            
            // Add size class if not default
            if (this.size !== 'md') {
                classes.push(`btn-${this.size}`)
            }
            
            // Add disabled class if disabled
            if (this.disabled || this.loading) {
                classes.push('disabled')
            }
            
            return classes.join(' ')
        },
        rel() {
            // Add security attributes for external links
            if (this.target === '_blank') {
                return 'noopener noreferrer'
            }
            return null
        },
        modalToggle() {
            return this.modal ? 'modal' : null
        },
        modalTarget() {
            return this.modal ? `#${this.modal}` : null
        }
    },
    methods: {
        handleClick() {
            if (!this.disabled && !this.loading) {
                this.$emit('click')
            }
        }
    }
})
</script>